import { Directive, effect, ElementRef, inject, input, OnDestroy, OnInit, output, signal, untracked } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { debounce, filter, Subject, takeUntil, timer } from 'rxjs';
import { ResizableSidePanelSettings, ResizableSidePanelSettingsEvent } from './resizable-side-panel-settings';
import { ResizableSidePanelService } from './resizable-side-panel.service';

interface ComponentWithElementRef {
  elementRef: ElementRef<HTMLElement>;
}

@Directive({
  selector: '[ezResizableSidePanel]',
  standalone: true,
  host: {
    class: 'resizable-panel',
  },
})
export class ResizableSidePanelDirective implements OnInit, OnDestroy {

  readonly ezResizableSidePanel = input.required<MatSidenav>();

  readonly enabled = input(true, { alias: 'ezResizableSidePanelEnabled' });
  readonly sidePanelDragHandle = input<HTMLElement | ElementRef<HTMLElement> | ComponentWithElementRef>();
  readonly sidePanelDefaultWidth = input<string>();
  readonly sidePanelSettings = input<ResizableSidePanelSettings | null | undefined>(undefined);
  readonly sidePanelSettingsChanged = output<ResizableSidePanelSettingsEvent>();

  readonly #handleMouseDown = this.#onMouseDown.bind(this);
  readonly #handleMouseMove = this.#onMouseMove.bind(this);
  readonly #handleMouseEnter = this.#onMouseEnter.bind(this);
  readonly #handleMouseLeave = this.#onMouseLeave.bind(this);
  readonly #handleTouchStart = this.#onTouchStart.bind(this);
  readonly #handleTouchMove = this.#onTouchMove.bind(this);

  previousX = 0;
  previousCursor = '';

  readonly #mouseOverChanged$ = new Subject<boolean>();
  readonly #cancelSubscriptions$ = new Subject<void>();

  readonly #width = signal<string>(this.sidePanelDefaultWidth());

  readonly #elementRef = inject(ElementRef);
  readonly #resizableSidePanelService = inject(ResizableSidePanelService);

  get #dragHandle(): HTMLElement | null {
    let handle = this.sidePanelDragHandle();
    if (!handle) {
      return null;
    }
    const handleAsComponent = handle as ComponentWithElementRef;
    if (handleAsComponent.elementRef) {
      handle = handleAsComponent.elementRef.nativeElement;
    }

    return handle instanceof ElementRef ? handle.nativeElement as HTMLElement : handle as HTMLElement;
  }

  constructor() {
    effect(() => {
      const width = this.sidePanelSettings()?.width ?? this.sidePanelDefaultWidth();
      untracked(() => this.#width.set(width));
    });
    effect(() => {
      this.#elementRef.nativeElement.style.width = this.#width();
      untracked(() => this.updateSettings({ width: this.#width() }));
    });
  }

  ngOnInit(): void {
    this.#resizableSidePanelService.initialize();
    this.#dragHandle.addEventListener('mousedown', this.#handleMouseDown);
    this.#dragHandle.addEventListener('touchstart', this.#handleTouchStart);
    this.#elementRef.nativeElement.addEventListener('mouseenter', this.#handleMouseEnter.bind(this));
    this.#elementRef.nativeElement.addEventListener('mouseleave', this.#handleMouseLeave.bind(this));

    this.#mouseOverChanged$
      .pipe(
        takeUntil(this.#cancelSubscriptions$),
        debounce(value => value ? timer(250) : timer(750)))
      .subscribe(value => this.#resizableSidePanelService.dragHandleEnabled.set(value));

    this.ezResizableSidePanel().openedChange
      .pipe(
        takeUntil(this.#cancelSubscriptions$),
        filter(opened => !opened))
      .subscribe(() => this.#resizableSidePanelService.initialize());
  }

  ngOnDestroy(): void {
    this.#dragHandle.removeEventListener('mousedown', this.#handleMouseDown);
    this.#dragHandle.removeEventListener('touchstart', this.#handleTouchStart);
    this.#elementRef.nativeElement.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.#elementRef.nativeElement.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.#unsetCursor();
    this.#cancelSubscriptions$.next();
  }

  #onMouseDown(event: MouseEvent): void {
    const html = document.documentElement;
    if (html.style.cursor !== 'ew-resize') {
      this.previousCursor = html.style.cursor;
    }
    html.style.cursor = 'ew-resize';
    event.preventDefault();
    window.addEventListener('mousemove', this.#handleMouseMove);
    window.addEventListener('mouseup', this.resizeEnd.bind(this), { once: true });
    this.#onResizeStart(event.x);
  }

  #onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      return;
    }
    event.preventDefault();
    window.addEventListener('touchmove', this.#handleTouchMove);
    window.addEventListener('touchend', this.resizeEnd.bind(this), { once: true });
    this.#onResizeStart(event.touches[0].clientX);
  }

  #onResizeStart(startingX: number): void {
    this.previousX = startingX;
    this.#dragHandle.classList.add('resizing');
    this.#resizableSidePanelService.isResizing.set(true);
  }

  #onMouseMove(event: MouseEvent): void {
    if (event.buttons !== 1) {
      return;
    }
    event.preventDefault();
    this.#onResizeEvent(event.x);
  }

  #onTouchMove(event: TouchEvent): void {
    if (event.touches.length !== 1) {
      return;
    }
    event.preventDefault();
    this.#onResizeEvent(event.touches[0].clientX);
  }

  #onResizeEvent(currentX: number): void {
    const diff = currentX - this.previousX;
    if (diff === 0) {
      return;
    }
    this.previousX = currentX;
    const target = this.#elementRef.nativeElement;
    const current = target.clientWidth;
    const next = current - diff;
    this.#width.set(`${next}px`);
  }

  resizeEnd(): void {
    this.#dragHandle.classList.remove('resizing');
    this.#dragHandle.removeEventListener('mousedown', this.#handleMouseMove);
    window.removeEventListener('mousemove', this.#handleMouseMove);
    this.#resizableSidePanelService.isResizing.set(false);
    this.#unsetCursor();
  }

  #onMouseEnter(): void {
    this.#mouseOverChanged$.next(true);
  }

  #onMouseLeave(): void {
    this.#mouseOverChanged$.next(false);
  }


  updateSettings(newSettings: Partial<ResizableSidePanelSettings>): void {
    const oldSettings = this.sidePanelSettings();
    const settings = { ...oldSettings, ...newSettings };
    this.sidePanelSettingsChanged.emit({ settings });
  }

  #unsetCursor(): void {
    const html = document.documentElement;
    html.style.cursor = this.previousCursor;
    this.previousCursor = '';
  }
}
